import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageContainer,
  Button,
  Badge,
  Textarea,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Alert,
  AlertDescription,
} from "@homy/ui";
import {
  ArrowLeft,
  Building2,
  User,
  Clock,
  PauseCircle,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Send,
  Copy,
  Check,
  ShieldCheck,
  RefreshCw,
  ExternalLink,
  MessageSquare,
  Info,
} from "lucide-react";
import {
  getPlatformTicket,
  updatePlatformTicketStatus,
  assignPlatformTicket,
  updatePlatformTicket,
  addPlatformTicketMessage,
  getPlatformOrganizationData,
} from "../lib/api/supportClient";
import { usePlatformAuth } from "../context/PlatformAuthContext";

export function PlatformSupportDetailPage() {
  const { id: ticketId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { operator } = usePlatformAuth();

  // State
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedTicketNo, setCopiedTicketNo] = useState(false);

  // Composer State
  const [replyContent, setReplyContent] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);

  // Status Modal State (for resolution notes)
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");

  // Query: Ticket Details
  const {
    data: ticket,
    isLoading: ticketLoading,
    error: ticketError,
    refetch: refetchTicket,
  } = useQuery({
    queryKey: ["platform-support-ticket", ticketId],
    queryFn: () => getPlatformTicket(ticketId),
    enabled: Boolean(ticketId),
  });

  // Query: Submitting Organization Profile Data (if org-affiliated)
  const { data: orgData } = useQuery({
    queryKey: ["platform-organization-data", ticket?.organizationId],
    queryFn: () => getPlatformOrganizationData(ticket.organizationId),
    enabled: Boolean(ticket?.organizationId),
  });

  // Mutation: Status Transition
  const statusMutation = useMutation({
    mutationFn: ({ status, notes }) =>
      updatePlatformTicketStatus(ticketId, {
        status,
        resolutionNotes: notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["platform-support-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["platform-support-metrics"] });
      setResolveModalOpen(false);
      setResolutionNotes("");
    },
  });

  // Mutation: Priority Update
  const priorityMutation = useMutation({
    mutationFn: (newPriority) =>
      updatePlatformTicket(ticketId, { priority: newPriority }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["platform-support-tickets"] });
    },
  });

  // Mutation: Assignee Update
  const assignMutation = useMutation({
    mutationFn: (assignedToId) => assignPlatformTicket(ticketId, assignedToId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["platform-support-tickets"] });
    },
  });

  // Mutation: Post Message / Note
  const messageMutation = useMutation({
    mutationFn: (payload) => addPlatformTicketMessage(ticketId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-support-ticket", ticketId] });
      setReplyContent("");
    },
  });

  const handleStatusChange = (newStatus) => {
    if (newStatus === "RESOLVED") {
      setResolveModalOpen(true);
    } else {
      statusMutation.mutate({ status: newStatus });
    }
  };

  const handleConfirmResolve = () => {
    statusMutation.mutate({
      status: "RESOLVED",
      notes: resolutionNotes.trim() || undefined,
    });
  };

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (!replyContent.trim()) return;

    messageMutation.mutate({
      content: replyContent.trim(),
      isInternal: isInternalNote,
    });
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === "token") {
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    } else {
      setCopiedTicketNo(true);
      setTimeout(() => setCopiedTicketNo(false), 2000);
    }
  };

  if (ticketLoading) {
    return (
      <PageContainer className="max-w-7xl mx-auto p-12 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-3">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <p>Loading platform ticket details...</p>
      </PageContainer>
    );
  }

  if (ticketError || !ticket) {
    return (
      <PageContainer className="max-w-7xl mx-auto p-12 text-center space-y-4">
        <AlertTriangle className="h-10 w-10 mx-auto text-rose-500" />
        <p className="text-lg font-semibold text-foreground">Platform Ticket Not Found</p>
        <p className="text-sm text-muted-foreground">
          The requested ticket does not exist or you do not have permission to inspect it.
        </p>
        <Button onClick={() => navigate("/support")}>Back to Support Queue</Button>
      </PageContainer>
    );
  }

  const isOrgAffiliated = Boolean(ticket.organizationId);
  const messages = ticket.messages || [];
  const ticketRef = ticket.caseNumber || ticket.referenceNumber || ticket.id;

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      {/* Top Navigation & Breadcrumbs */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => navigate("/support")}
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Support Queue
        </Button>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => refetchTicket()}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Main Ticket Control Header */}
      <div className="bg-card border border-border p-6 rounded-2xl shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold font-mono tracking-tight text-foreground">
                {ticketRef}
              </h1>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                title="Copy ticket number"
                onClick={() => copyToClipboard(ticketRef, "ref")}
              >
                {copiedTicketNo ? (
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>

              <Badge
                variant="outline"
                className="border-primary/30 bg-primary/10 text-primary font-mono text-xs uppercase"
              >
                PLATFORM TRACK
              </Badge>
            </div>

            <p className="text-lg font-medium text-foreground">
              {ticket.subject || "No Subject Specified"}
            </p>
          </div>

          {/* Quick Action Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Transition Selector */}
            <div className="space-y-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block font-semibold">
                Status
              </span>
              <select
                value={ticket.status === "WAITING" ? "WAITING_CUSTOMER" : ticket.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={statusMutation.isPending}
                className="h-9 rounded-lg border border-border bg-input px-3 text-xs font-semibold text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="NEW">NEW</option>
                <option value="OPEN">OPEN</option>
                <option value="IN_PROGRESS">IN PROGRESS</option>
                <option value="WAITING_CUSTOMER">WAITING CUSTOMER</option>
                <option value="RESOLVED">RESOLVED</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </div>

            {/* Priority Selector */}
            <div className="space-y-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block font-semibold">
                Priority
              </span>
              <select
                value={ticket.priority}
                onChange={(e) => priorityMutation.mutate(e.target.value)}
                disabled={priorityMutation.isPending}
                className="h-9 rounded-lg border border-border bg-input px-3 text-xs font-semibold text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="URGENT">URGENT</option>
                <option value="HIGH">HIGH</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="LOW">LOW</option>
              </select>
            </div>

            {/* Assignee Selector */}
            <div className="space-y-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block font-semibold">
                Assignee
              </span>
              <div className="flex items-center gap-1.5">
                {ticket.assignedToId === operator?.id ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs border-amber-500/30 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20"
                    onClick={() => assignMutation.mutate(null)}
                    disabled={assignMutation.isPending}
                  >
                    Assigned to You (Unassign)
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs gap-1"
                    onClick={() => assignMutation.mutate(operator?.id || "operator")}
                    disabled={assignMutation.isPending}
                  >
                    <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Assign to Me
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid: Left Context Columns (1/3) & Right Timeline (2/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Context & Metadata */}
        <div className="lg:col-span-4 space-y-6">
          {/* Submitter & Organization Metadata Card */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  {isOrgAffiliated ? (
                    <>
                      <Building2 className="h-4 w-4 text-primary" /> Submitting Organization
                    </>
                  ) : (
                    <>
                      <User className="h-4 w-4 text-purple-400" /> Public Quarantine Submitter
                    </>
                  )}
                </CardTitle>
                <Badge
                  variant="outline"
                  className={
                    isOrgAffiliated
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-[10px]"
                      : "border-purple-500/30 bg-purple-500/10 text-purple-400 text-[10px]"
                  }
                >
                  {isOrgAffiliated ? "Tenant Affiliated" : "Quarantined Guest"}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 text-xs">
              {isOrgAffiliated ? (
                <>
                  <div className="space-y-1">
                    <p className="text-[10px] text-muted-foreground uppercase font-mono">
                      Organization
                    </p>
                    <div className="flex items-center justify-between font-medium text-foreground">
                      <span>{orgData?.organization?.name || "Loading name..."}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[11px] gap-1 text-primary hover:text-primary/80"
                        onClick={() => navigate(`/organizations`)}
                      >
                        View <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                    <p className="font-mono text-[11px] text-muted-foreground truncate">
                      ID: {ticket.organizationId}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/50">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase font-mono">Tier</p>
                      <p className="font-semibold text-foreground">
                        {orgData?.organization?.tier || "PRO"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase font-mono">Country</p>
                      <p className="font-semibold text-foreground font-mono">
                        {orgData?.organization?.countryCode || "CL"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase font-mono">Users</p>
                      <p className="font-semibold text-foreground">
                        {orgData?.usersCount ?? "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase font-mono">Properties</p>
                      <p className="font-semibold text-foreground">
                        {orgData?.propertiesCount ?? "—"}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-border/50 space-y-1">
                    <p className="text-[10px] text-muted-foreground uppercase font-mono">
                      Submitter User
                    </p>
                    <p className="font-medium text-foreground font-mono">
                      {ticket.userId ? `User: ${ticket.userId}` : ticket.guestEmail || "Staff Member"}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Alert className="border-purple-500/30 bg-purple-500/5 text-purple-300 py-2">
                    <Info className="h-4 w-4 text-purple-400" />
                    <AlertDescription className="text-[11px] leading-relaxed">
                      Zero-Leakage Quarantine: This inquiry originated without an organization
                      context. It is routed to the sovereign platform track to prevent cross-tenant
                      data leakage (INV-03).
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-1">
                    <p className="text-[10px] text-muted-foreground uppercase font-mono">Guest Name</p>
                    <p className="font-medium text-foreground">{ticket.guestName || "Guest User"}</p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-[10px] text-muted-foreground uppercase font-mono">Guest Email</p>
                    <p className="font-medium text-foreground font-mono">
                      {ticket.guestEmail || "None provided"}
                    </p>
                  </div>

                  {ticket.guestPhone && (
                    <div className="space-y-1">
                      <p className="text-[10px] text-muted-foreground uppercase font-mono">Phone</p>
                      <p className="font-medium text-foreground font-mono">{ticket.guestPhone}</p>
                    </div>
                  )}

                  {ticket.trackingToken && (
                    <div className="pt-2 border-t border-border/50 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-muted-foreground uppercase font-mono">
                          Public Tracking UUID
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-muted-foreground hover:text-foreground"
                          onClick={() => copyToClipboard(ticket.trackingToken, "token")}
                        >
                          {copiedToken ? (
                            <Check className="h-3.5 w-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                      <p className="font-mono text-[10px] text-muted-foreground break-all">
                        {ticket.trackingToken}
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Operational & SLA Lifecycle Card */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" /> SLA & Operational Lifecycle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div className="space-y-1">
                <p className="text-[10px] text-muted-foreground uppercase font-mono">Created At</p>
                <p className="font-mono text-foreground">
                  {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : "—"}
                </p>
              </div>

              {ticket.firstResponseAt ? (
                <div className="space-y-1">
                  <p className="text-[10px] text-muted-foreground uppercase font-mono">
                    First Response
                  </p>
                  <p className="font-mono text-foreground">
                    {new Date(ticket.firstResponseAt).toLocaleString()}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-[10px] text-muted-foreground uppercase font-mono">
                    First Response
                  </p>
                  <p className="font-mono text-amber-400">Awaiting operator response</p>
                </div>
              )}

              <div className="space-y-1 pt-2 border-t border-border/50">
                <p className="text-[10px] text-muted-foreground uppercase font-mono">SLA Deadline</p>
                <p className="font-mono text-foreground">
                  {ticket.slaExpiresAt ? new Date(ticket.slaExpiresAt).toLocaleString() : "—"}
                </p>
              </div>

              {ticket.status === "WAITING_CUSTOMER" && (
                <Alert className="border-amber-500/30 bg-amber-500/10 text-amber-300 py-2">
                  <PauseCircle className="h-4 w-4 text-amber-400" />
                  <AlertDescription className="text-[11px]">
                    SLA Clock is paused. Resumes automatically when the customer replies or status
                    re-enters OPEN (SLA-INV-01).
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1">
                <p className="text-[10px] text-muted-foreground uppercase font-mono">
                  Total Paused Duration
                </p>
                <p className="font-mono text-foreground">
                  {ticket.totalPausedDurationMinutes ?? 0} minutes
                </p>
              </div>

              {ticket.resolvedAt && (
                <div className="space-y-1 pt-2 border-t border-border/50">
                  <p className="text-[10px] text-muted-foreground uppercase font-mono">Resolved At</p>
                  <p className="font-mono text-emerald-400">
                    {new Date(ticket.resolvedAt).toLocaleString()}
                  </p>
                  {ticket.resolutionNotes && (
                    <div className="bg-muted/30 p-2 rounded text-[11px] text-foreground mt-1">
                      {ticket.resolutionNotes}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Original Issue, Timeline & Composer */}
        <div className="lg:col-span-8 space-y-6">
          {/* Initial Problem Description Card */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" /> Initial Inquiry
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px] uppercase font-mono">
                    {ticket.category || "GENERAL"}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground font-mono">
                    via {ticket.channel || "WEB"}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/20 border border-border/60 rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap text-foreground">
                {ticket.description || ticket.initialMessage || "No description provided."}
              </div>
            </CardContent>
          </Card>

          {/* Conversation Timeline */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" /> Thread Timeline (
                {messages.length} messages)
              </h3>
            </div>

            {messages.length === 0 ? (
              <div className="p-8 border border-border/60 rounded-xl text-center text-xs text-muted-foreground bg-card">
                No replies or notes recorded yet. Use the composer below to respond.
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isInternal = Boolean(msg.isInternal);
                  const isOutbound =
                    msg.direction === "OUTBOUND" || msg.senderRole === "PLATFORM_ADMIN";

                  // Message Bubble Styling based on type
                  if (isInternal) {
                    return (
                      <div
                        key={msg.id}
                        className="rounded-xl border border-amber-500/40 bg-amber-950/20 dark:bg-amber-950/40 p-4 space-y-2 shadow-sm"
                      >
                        <div className="flex items-center justify-between border-b border-amber-500/20 pb-2">
                          <div className="flex items-center gap-1.5 text-amber-300 font-semibold text-xs">
                            <Lock className="h-3.5 w-3.5 text-amber-400" />
                            <span>INTERNAL OPERATOR NOTE</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="border-amber-500/30 bg-amber-500/10 text-amber-400 text-[10px] font-mono"
                            >
                              Confidential Staff Only
                            </Badge>
                            <span className="text-[10px] font-mono text-muted-foreground">
                              {new Date(msg.sentAt).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-amber-100 whitespace-pre-wrap leading-relaxed">
                          {msg.content}
                        </p>
                      </div>
                    );
                  }

                  if (isOutbound) {
                    return (
                      <div
                        key={msg.id}
                        className="ml-6 rounded-xl border border-primary/30 bg-primary/10 p-4 space-y-2 shadow-sm"
                      >
                        <div className="flex items-center justify-between border-b border-primary/20 pb-2">
                          <div className="flex items-center gap-1.5 text-primary font-semibold text-xs">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            <span>Platform Support Operator</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] font-mono">
                              Public Reply
                            </Badge>
                            <span className="text-[10px] font-mono text-muted-foreground">
                              {new Date(msg.sentAt).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">
                          {msg.content}
                        </p>
                      </div>
                    );
                  }

                  // Inbound customer reply
                  return (
                    <div
                      key={msg.id}
                      className="mr-6 rounded-xl border border-border bg-card p-4 space-y-2 shadow-sm"
                    >
                      <div className="flex items-center justify-between border-b border-border/60 pb-2">
                        <div className="flex items-center gap-1.5 text-foreground font-semibold text-xs">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>Submitter Response</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px] font-mono">
                            Inbound ({msg.channel || "WEB"})
                          </Badge>
                          <span className="text-[10px] font-mono text-muted-foreground">
                            {new Date(msg.sentAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">
                        {msg.content}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reply & Internal Note Composer */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Send className="h-4 w-4 text-primary" /> Composer
                </CardTitle>

                {/* Mode Selector Toggle */}
                <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() => setIsInternalNote(false)}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      !isInternalNote
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Reply to Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsInternalNote(true)}
                    className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium transition-colors ${
                      isInternalNote
                        ? "bg-amber-500/20 text-amber-300 border border-amber-500/30 shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Lock className="h-3 w-3" /> Internal Staff Note
                  </button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-3">
              {isInternalNote ? (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs text-amber-300 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-amber-400 shrink-0" />
                  <span>
                    Confidential internal staff note. Visible only to platform operators. Redacted
                    from customer tracking (INV-05).
                  </span>
                </div>
              ) : (
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5 text-xs text-primary flex items-center gap-2">
                  <Info className="h-4 w-4 shrink-0" />
                  <span>
                    Public response. Delivered to submitter and visible on their tracking portal.
                  </span>
                </div>
              )}

              <Textarea
                rows={4}
                placeholder={
                  isInternalNote
                    ? "Add an internal operator note regarding diagnosis, escalation, or work order..."
                    : "Type a response to the customer..."
                }
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className={`bg-input text-sm ${
                  isInternalNote
                    ? "border-amber-500/40 focus:ring-amber-500 text-amber-100"
                    : "border-border focus:ring-primary"
                }`}
              />

              <div className="flex items-center justify-between pt-1">
                <span className="text-[11px] text-muted-foreground font-mono">
                  Press Send to submit
                </span>

                <Button
                  onClick={handleSendMessage}
                  disabled={!replyContent.trim() || messageMutation.isPending}
                  className={`gap-1.5 text-xs font-semibold ${
                    isInternalNote
                      ? "bg-amber-600 hover:bg-amber-700 text-white"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground"
                  }`}
                >
                  {messageMutation.isPending ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : isInternalNote ? (
                    <>
                      <Lock className="h-3.5 w-3.5" /> Record Internal Note
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5" /> Send Reply to Customer
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Resolution Notes Modal */}
      <Dialog open={resolveModalOpen} onOpenChange={setResolveModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
              <DialogTitle>Resolve Platform Ticket</DialogTitle>
            </div>
            <DialogDescription>
              Mark ticket {ticketRef} as RESOLVED. Please provide optional or mandatory resolution
              details for the audit trail.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 my-2">
            <Label htmlFor="resolutionNotes" className="text-xs">
              Resolution Summary
            </Label>
            <Textarea
              id="resolutionNotes"
              rows={3}
              placeholder="Describe the steps taken to resolve this platform inquiry..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              className="bg-input text-xs"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResolveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleConfirmResolve}
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending ? "Resolving..." : "Confirm & Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export default PlatformSupportDetailPage;
